import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  Sparkles, LogOut, ShieldCheck, ChevronDown, AppWindow,
  NotebookPen, Wand2, Globe, Gamepad2, BookOpen, BookMarked,
  LayoutGrid, Images, Hammer, Search,
} from "lucide-react";
import { api } from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";

const BRAND = "https://customer-assets.emergentagent.com/job_1a752c50-99ae-44d4-a5d5-08c385e2832e/artifacts/enaxu6ve_Product%402x.png";
const ADMIN_DOMAIN = "@durgaprogrammingadventures.com";

// icon-key → Lucide component. Extend as we register more apps.
const APP_ICONS = {
  "notebook-pen": NotebookPen,
  "hammer": Hammer,
  "wand-2": Wand2,
  "globe": Globe,
  "gamepad-2": Gamepad2,
  "book-open": BookOpen,
  "book-marked": BookMarked,
  "layout-grid": LayoutGrid,
  "images": Images,
  "sparkles": Sparkles,
};

const CATEGORY_ORDER = ["Build", "Learn", "Explore", "Account"];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [apps, setApps] = useState([]);
  const [appsOpen, setAppsOpen] = useState(false);

  useEffect(() => {
    api.get("/apps").then(({ data }) => setApps(data.apps || [])).catch(() => setApps([]));
  }, []);

  // Close mega-menu on route change
  useEffect(() => { setAppsOpen(false); }, [loc.pathname]);

  const grouped = useMemo(() => {
    const g = {};
    apps.forEach((a) => {
      const cat = a.category || "Other";
      (g[cat] ||= []).push(a);
    });
    return g;
  }, [apps]);

  const isAppsRoute = apps.some((a) => a.route === loc.pathname || (a.route !== "/" && loc.pathname.startsWith(a.route)));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-cream/80 border-b border-royal/15 shadow-[0_1px_0_0_rgba(201,162,74,0.15)]" data-testid="navbar">
      <div className="container-narrow flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <span className="relative inline-block w-9 h-9 rounded-full overflow-hidden ring-2 ring-gold/50 ring-offset-2 ring-offset-cream group-hover:ring-gold transition flex-shrink-0">
            <img src={BRAND} alt="Durga" className="w-full h-full object-cover" />
          </span>
          <span className="font-hand text-[24px] md:text-[26px] font-bold text-royal leading-none whitespace-nowrap tracking-tight">
            Durga <span className="text-marigold">Programming</span> Adventures
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5">
          {[
            { to: "/learn",      label: "Learn",      testid: "nav-learn" },
            { to: "/adventures", label: "Adventures", testid: "nav-adventures" },
            { to: "/library",    label: "Library",    testid: "nav-library" },
            { to: "/lab",        label: "Labs",       testid: "nav-lab" },
          ].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `relative px-3 py-2 rounded-full text-sm font-medium transition-colors group ${
                  isActive ? "text-ink bg-paper" : "text-ink2 hover:text-ink"
                }`
              }
              data-testid={it.testid}
            >
              <span className="relative">
                {it.label}
                <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-marigold scale-x-0 group-hover:scale-x-100 origin-center transition-transform" />
              </span>
            </NavLink>
          ))}

          {/* Apps mega-menu — everything else lives here */}
          <div
            className="relative"
            onMouseEnter={() => setAppsOpen(true)}
            onMouseLeave={() => setAppsOpen(false)}
            data-testid="nav-apps"
          >
            <button
              className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                isAppsRoute ? "text-ink bg-paper" : "text-ink2 hover:text-ink"
              }`}
              aria-expanded={appsOpen}
              onClick={() => setAppsOpen((v) => !v)}
              data-testid="nav-apps-trigger"
            >
              Apps <ChevronDown className={`w-3.5 h-3.5 transition-transform ${appsOpen ? "rotate-180" : ""}`} />
            </button>

            {appsOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-[720px] max-w-[92vw] rounded-2xl border border-border bg-cream shadow-[0_28px_60px_-15px_rgba(26,27,65,0.28)] overflow-hidden"
                data-testid="nav-apps-menu"
              >
                <div className="px-6 pt-5 pb-3 border-b border-border/60 flex items-center justify-between">
                  <div>
                    <div className="label-eyebrow inline-flex items-center gap-2"><AppWindow className="w-3 h-3" /> Everything Durga</div>
                    <p className="text-xs text-ink2 mt-1">Build tools, learning material, and your library — all in one place.</p>
                  </div>
                  <Link
                    to="/apps"
                    className="text-xs text-ink2 hover:text-marigold inline-flex items-center gap-1"
                    data-testid="nav-apps-see-all"
                  >
                    See all <ChevronDown className="w-3 h-3 -rotate-90" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-4 p-4 max-h-[70vh] overflow-y-auto">
                  {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
                    <div key={cat} className="col-span-2">
                      <div className="px-2 mb-1.5 text-[10px] uppercase tracking-widest font-bold text-ink2">{cat}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {grouped[cat].map((app) => {
                          const Icon = APP_ICONS[app.icon] || AppWindow;
                          return (
                            <Link
                              key={app.slug}
                              to={app.route}
                              className="flex gap-3 p-3 rounded-xl hover:bg-paper transition group"
                              data-testid={`nav-apps-item-${app.slug}`}
                            >
                              <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${app.accent || "#2A9D8F"}22`, color: app.accent || "#2A9D8F" }}
                              >
                                <Icon className="w-5 h-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-heading font-semibold text-ink text-sm">{app.name}</span>
                                  {app.status && app.status !== "live" && (
                                    <span className="text-[9px] uppercase tracking-widest font-bold text-marigold bg-marigold/10 rounded px-1.5 py-0.5">{app.status}</span>
                                  )}
                                </div>
                                <p className="text-[11px] text-ink2 leading-snug mt-0.5 line-clamp-2">{app.tagline}</p>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/search"
            className="w-9 h-9 rounded-full border border-ink/25 hover:border-marigold hover:text-marigold text-ink2 flex items-center justify-center transition"
            title="Search"
            data-testid="nav-search"
          >
            <Search className="w-4 h-4" />
          </Link>
          <LanguageSwitcher />
          {user ? (
            <>
              {user.email?.toLowerCase().endsWith(ADMIN_DOMAIN) && (
                <Link
                  to="/admin"
                  className="btn-ghost text-sm hidden sm:inline-flex text-marigold hover:text-marigold/80"
                  data-testid="nav-admin"
                >
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-ghost text-sm" data-testid="nav-dashboard">
                <Sparkles className="w-4 h-4" /> {user.name?.split(" ")[0] || "You"}
              </Link>
              <button
                className="btn-ghost text-sm"
                onClick={() => { logout(); nav("/"); }}
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/signup" className="btn-primary text-sm py-2 px-5" data-testid="nav-signup">
                {t("nav.signup")}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
